import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Thermometer, PackageOpen, SprayCanIcon, ClipboardList,
  ChevronRight, Bell, AlertTriangle, Sparkles, Gift, X, FileWarning
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationCenter = () => {
  const { getAuthHeaders, localeCorrente } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null);
  const [yolloNotifs, setYolloNotifs] = useState([]);

  useEffect(() => {
    if (!localeCorrente) return;
    const fetchNotifications = async () => {
      try {
        const headers = getAuthHeaders();
        const [res, notifsRes] = await Promise.all([
          axios.get(`${API}/dashboard/notifications?locale_id=${localeCorrente.id}`, { headers }),
          axios.get(`${API}/notifications`, { headers }).catch(() => ({ data: [] })),
        ]);
        setData(res.data);
        setYolloNotifs((notifsRes.data || []).filter(n => !n.letto));
      } catch (e) {
        console.error("Error fetching notifications:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, [localeCorrente]);

  if (loading || (!data && yolloNotifs.length === 0) || (data?.totale_notifiche === 0 && yolloNotifs.length === 0)) return null;

  const markYolloRead = async (notifId) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/notifications/${notifId}/read`, {}, { headers });
      setYolloNotifs(prev => prev.filter(n => n.id !== notifId));
    } catch {}
  };

  const toggleSection = (key) => {
    setExpandedSection(expandedSection === key ? null : key);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  };

  const timeAgo = (isoStr) => {
    if (!isoStr) return "";
    const diff = Date.now() - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m fa`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h fa`;
    const days = Math.floor(hrs / 24);
    return `${days}g fa`;
  };

  const sections = [
    {
      key: "temperature",
      label: "Alert Temperature",
      icon: Thermometer,
      count: data.temperature.count,
      color: "#ef4444",
      bgColor: "#fef2f2",
      borderColor: "#fecaca",
      link: "/frigoriferi",
      linkLabel: "Controllo Temperature",
      previewText: data.temperature.items.slice(0, 3).map(a => a.frigorifero_nome).join(", "),
      renderItems: () => data.temperature.items.map((a) => (
        <div key={a.id} onClick={() => navigate("/frigoriferi")}
          className="flex items-center justify-between py-2 px-3 rounded-xl bg-white/70 cursor-pointer hover:bg-white transition-colors group/item"
          data-testid={`notif-temp-${a.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-800 truncate group-hover/item:text-red-600 transition-colors">{a.frigorifero_nome}</span>
              <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
                a.stato === "da_tecnico" ? "bg-red-500 text-white" :
                a.stato === "in_attesa" ? "bg-amber-400 text-black" :
                "bg-red-400 text-white"
              }`}>
                {a.stato === "da_tecnico" ? "TECNICO" : a.stato === "in_attesa" ? "VERIFICA" : "APERTO"}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {a.temperatura}°C (range: {a.range_min}° / {a.range_max}°C)
              {a.nota_anomalia && <span className="ml-1">- {a.nota_anomalia}</span>}
            </p>
          </div>
          <div className="flex items-center gap-1.5 ml-2 shrink-0">
            <span className="text-[10px] text-gray-400">{timeAgo(a.created_at)}</span>
            <ChevronRight className="h-3.5 w-3.5 text-gray-300 group-hover/item:text-red-400 transition-colors" />
          </div>
        </div>
      ))
    },
    {
      key: "scorte",
      label: "Scorte & Riordini",
      icon: PackageOpen,
      count: data.scorte.count,
      color: "#f59e0b",
      bgColor: "#fffbeb",
      borderColor: "#fde68a",
      link: "/acquisti/food",
      linkLabel: "Gestisci Acquisti",
      previewText: data.scorte.items.slice(0, 3).map(a => a.prodotto_nome).join(", "),
      renderItems: () => data.scorte.items.map((a) => (
        <div key={a.id} onClick={() => navigate("/acquisti/food")}
          className="flex items-center justify-between py-2 px-3 rounded-xl bg-white/70 cursor-pointer hover:bg-white transition-colors group/item"
          data-testid={`notif-stock-${a.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-800 truncate group-hover/item:text-amber-600 transition-colors">{a.prodotto_nome}</span>
              <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
                a.urgenza === "esaurito" ? "bg-red-500 text-white" : "bg-amber-400 text-black"
              }`}>
                {a.urgenza === "esaurito" ? "ESAURITO" : "IN ESAURIMENTO"}
              </span>
            </div>
            {a.note && <p className="text-xs text-gray-500 mt-0.5 truncate">{a.note}</p>}
            <p className="text-xs text-gray-400 mt-0.5">Segnalato da {a.creato_da_nome}</p>
          </div>
          <div className="flex items-center gap-1.5 ml-2 shrink-0">
            <span className="text-[10px] text-gray-400">{timeAgo(a.created_at)}</span>
            <ChevronRight className="h-3.5 w-3.5 text-gray-300 group-hover/item:text-amber-400 transition-colors" />
          </div>
        </div>
      ))
    },
    {
      key: "pulizie",
      label: "Pulizie Non Completate",
      icon: SprayCanIcon,
      count: data.pulizie_mancate.count,
      color: "#8b5cf6",
      bgColor: "#f5f3ff",
      borderColor: "#ddd6fe",
      link: "/programma-pulizie",
      linkLabel: "Piano Pulizie",
      renderItems: () => data.pulizie_mancate.items.map((t) => (
        <div key={t.id} onClick={() => navigate("/programma-pulizie")}
          className="flex items-center justify-between py-2 px-3 rounded-xl bg-white/70 cursor-pointer hover:bg-white transition-colors group/item"
          data-testid={`notif-clean-${t.id}`}>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-semibold text-gray-800 group-hover/item:text-violet-600 transition-colors">{t.nome}</span>
            {t.area && <span className="text-xs text-gray-500 ml-2">({t.area})</span>}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-[10px] text-gray-400">{formatDate(t.data)}</span>
            <ChevronRight className="h-3.5 w-3.5 text-gray-300 group-hover/item:text-violet-400 transition-colors" />
          </div>
        </div>
      ))
    },
    {
      key: "compiti",
      label: "Compiti Non Completati",
      icon: ClipboardList,
      count: data.compiti_mancati.count,
      color: "#3b82f6",
      bgColor: "#eff6ff",
      borderColor: "#bfdbfe",
      link: "/task-gestione",
      linkLabel: "Bacheca Compiti",
      renderItems: () => data.compiti_mancati.items.map((t) => (
        <div key={t.id} onClick={() => navigate("/task-gestione")}
          className="flex items-center justify-between py-2 px-3 rounded-xl bg-white/70 cursor-pointer hover:bg-white transition-colors group/item"
          data-testid={`notif-task-${t.id}`}>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-semibold text-gray-800 group-hover/item:text-blue-600 transition-colors">{t.nome}</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-[10px] text-gray-400">{formatDate(t.data)}</span>
            <ChevronRight className="h-3.5 w-3.5 text-gray-300 group-hover/item:text-blue-400 transition-colors" />
          </div>
        </div>
      ))
    },
    {
      key: "documenti",
      label: "Documenti in Scadenza",
      icon: FileWarning,
      count: data.documenti_scadenze?.count || 0,
      color: "#e11d48",
      bgColor: "#fff1f2",
      borderColor: "#fecdd3",
      link: "/archivio-documenti",
      linkLabel: "Archivio Documenti",
      previewText: (data.documenti_scadenze?.items || []).slice(0, 3).map(d => 
        `${d.nome}${d.dipendente_nome ? ` (${d.dipendente_nome})` : ""}`
      ).join(", "),
      renderItems: () => (data.documenti_scadenze?.items || []).map((d) => (
        <div key={d.id} onClick={() => navigate("/archivio-documenti")}
          className="flex items-center justify-between py-2 px-3 rounded-xl bg-white/70 cursor-pointer hover:bg-white transition-colors group/item"
          data-testid={`notif-doc-${d.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-800 truncate group-hover/item:text-rose-600 transition-colors">
                {d.nome}{d.dipendente_nome ? ` - ${d.dipendente_nome}` : ""}
              </span>
              <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
                d.scaduto ? "bg-red-500 text-white" : "bg-amber-400 text-black"
              }`}>
                {d.scaduto ? "SCADUTO" : "IN SCADENZA"}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {d.tipo === "contratto" ? "Contratto" : "Documento"} - Scadenza: {formatDate(d.data_scadenza)}
            </p>
          </div>
          <ChevronRight className="h-3.5 w-3.5 text-gray-300 group-hover/item:text-rose-400 transition-colors ml-2 shrink-0" />
        </div>
      ))
    }
  ].filter(s => s.count > 0);

  if (sections.length === 0 && yolloNotifs.length === 0) return null;

  const hasCritical = data.temperature.critici > 0 || (data.documenti_scadenze?.scaduti || 0) > 0;

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: hasCritical
          ? "linear-gradient(135deg, #fef2f2 0%, #fff7ed 50%, #fefce8 100%)"
          : "linear-gradient(135deg, #fff7ed 0%, #fefce8 50%, #f0fdf4 100%)",
        border: `1px solid ${hasCritical ? "#fecaca" : "#fde68a"}`,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)"
      }}
      data-testid="notification-center"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
            hasCritical ? "bg-red-100" : "bg-amber-100"
          }`}>
            <Bell className={`h-4.5 w-4.5 ${hasCritical ? "text-red-500" : "text-amber-500"}`} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-800">Centro Notifiche</h3>
            <p className="text-[11px] text-gray-500">{data.totale_notifiche} {data.totale_notifiche === 1 ? "notifica attiva" : "notifiche attive"}</p>
          </div>
        </div>
        {/* Category badges summary */}
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {sections.map((s) => (
            <button
              key={s.key}
              onClick={() => toggleSection(s.key)}
              className="flex items-center gap-1 px-2 py-1 rounded-lg transition-all hover:scale-105"
              style={{
                backgroundColor: expandedSection === s.key ? s.color : `${s.color}15`,
                color: expandedSection === s.key ? "white" : s.color,
              }}
              data-testid={`notif-badge-${s.key}`}
            >
              <s.icon className="h-3.5 w-3.5" />
              <span className="text-xs font-bold">{s.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Yollo promo/regalo notifications */}
      {yolloNotifs.length > 0 && (
        <div className="px-4 pb-2 space-y-2">
          {yolloNotifs.map(notif => (
            <div key={notif.id} className="relative rounded-xl overflow-hidden transition-all hover:scale-[1.01]"
              style={{
                background: notif.tipo === "regalo" ? "linear-gradient(135deg, #ecfdf5, #d1fae5)" :
                  notif.tipo === "promo" ? "linear-gradient(135deg, #fffbeb, #fef3c7)" :
                  notif.tipo === "segnalazione_risolta" ? "linear-gradient(135deg, #f0fdf4, #dcfce7)" :
                  "linear-gradient(135deg, #eff6ff, #dbeafe)",
                border: `1.5px solid ${notif.tipo === "regalo" ? "#86efac" : notif.tipo === "promo" ? "#fde68a" : notif.tipo === "segnalazione_risolta" ? "#86efac" : "#93c5fd"}`,
              }}
              data-testid={`yollo-notif-${notif.id}`}>
              <Link to={notif.link || "/abbonamento"} onClick={() => markYolloRead(notif.id)} className="block">
                <div className="flex items-center gap-3 px-4 py-3 pr-10">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${notif.tipo === "regalo" ? "bg-emerald-200" : notif.tipo === "promo" ? "bg-amber-200" : notif.tipo === "segnalazione_risolta" ? "bg-green-200" : "bg-blue-200"}`}>
                    {notif.tipo === "regalo" ? <Gift className="h-5 w-5 text-emerald-600" /> :
                     notif.tipo === "promo" ? <Sparkles className="h-5 w-5 text-amber-600" /> :
                     notif.tipo === "segnalazione_risolta" ? <Bell className="h-5 w-5 text-green-600" /> :
                     <Bell className="h-5 w-5 text-blue-600" />}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-black ${notif.tipo === "regalo" ? "text-emerald-700" : notif.tipo === "promo" ? "text-amber-700" : notif.tipo === "segnalazione_risolta" ? "text-green-700" : "text-blue-700"}`}>{notif.titolo}</p>
                    <p className="text-xs text-gray-600 mt-0.5">{notif.messaggio}</p>
                  </div>
                </div>
              </Link>
              <button onClick={(e) => { e.stopPropagation(); markYolloRead(notif.id); }} 
                className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-sm transition-colors" data-testid={`dismiss-notif-${notif.id}`}>
                <X className="h-3.5 w-3.5 text-gray-400" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Sections */}
      <div className="px-4 pb-4 space-y-2">
        {sections.map((section) => {
          const isExpanded = expandedSection === section.key;
          return (
            <div
              key={section.key}
              className="rounded-xl overflow-hidden transition-all"
              style={{
                backgroundColor: isExpanded ? section.bgColor : "rgba(255,255,255,0.6)",
                border: `1px solid ${isExpanded ? section.borderColor : "rgba(0,0,0,0.06)"}`,
              }}
            >
              {/* Section header - clickable: tap to expand, double tap or arrow to navigate */}
              <button
                onClick={() => toggleSection(section.key)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/40 transition-colors"
                data-testid={`notif-section-${section.key}`}
              >
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${section.color}15` }}>
                    <section.icon className="h-3.5 w-3.5" style={{ color: section.color }} />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-700">{section.label}</span>
                      <span
                        className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${section.color}20`, color: section.color }}
                      >
                        {section.count}
                      </span>
                    </div>
                    {/* Preview: mostra i primi item se la sezione è collassata */}
                    {!isExpanded && section.previewText && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{section.previewText}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Direct navigation button */}
                  <span
                    onClick={(e) => { e.stopPropagation(); navigate(section.link); }}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-lg transition-all hover:scale-105"
                    style={{ backgroundColor: `${section.color}15`, color: section.color }}
                    data-testid={`notif-goto-${section.key}`}
                  >
                    Vai
                  </span>
                  <ChevronRight
                    className="h-4 w-4 text-gray-400 transition-transform"
                    style={{ transform: isExpanded ? "rotate(90deg)" : "none" }}
                  />
                </div>
              </button>

              {/* Expanded items - each clickable */}
              {isExpanded && (
                <div className="px-3 pb-3 space-y-1.5">
                  {section.renderItems()}
                  <Link
                    to={section.link}
                    className="flex items-center justify-center gap-1.5 py-2 mt-1 rounded-xl text-xs font-semibold transition-colors hover:bg-white/60"
                    style={{ color: section.color }}
                    data-testid={`notif-link-${section.key}`}
                  >
                    {section.linkLabel}
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NotificationCenter;
